import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faWineBottle,
  faCocktail,
  faShoppingCart,
  faSearch,
  faPlus,
  faWineGlassAlt,
  faEllipsisV,
  faLemon,
  faTimes,
  faSlidersH,
  faBookmark,
} from '@fortawesome/free-solid-svg-icons';

const iconMap = {
  faLemon: faLemon,
  faWineBottle: faWineBottle,
  faTimes: faTimes,
};

function MyBarPage({ userData, apiClient }) {
  const navigate = useNavigate();
  const [data, setData] = useState({
    total_ingredients: 0,
    total_drinks: 0,
    ingredient_categories: [],
    favorite_ingredients: [],
    ingredients: [],
  });
  const [localInventory, setLocalInventory] = useState({}); // Tracks { ingredientid: amount }
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiClient.request('/api/mybar', { method: 'GET' });
        console.log('API response:', response);
        // Sanitize data
        const sanitizedData = {
          total_ingredients: response.total_ingredients || 0,
          total_drinks: response.total_drinks || 0,
          ingredient_categories: Array.isArray(response.ingredient_categories) ? response.ingredient_categories : [],
          favorite_ingredients: Array.isArray(response.favorite_ingredients)
            ? response.favorite_ingredients.map(item => ({
                ...item,
                icon: item.icon || 'faWineBottle',
                icon_color: item.icon_color || 'ingredients-gray',
                type_name: item.type_name || 'Unknown',
              }))
            : [],
          ingredients: Array.isArray(response.ingredients)
            ? response.ingredients.map(item => ({
                ...item,
                icon: item.icon || 'faWineBottle',
                icon_color: item.icon_color || 'ingredients-gray',
                type_name: item.type_name || 'Unknown',
                amount: item.amount || 0,
              }))
            : [],
        };
        setData(sanitizedData);
        // Initialize localInventory from ingredients (amount > 0)
        const inventoryMap = {};
        sanitizedData.ingredients.forEach(item => {
          inventoryMap[item.ingredientid] = item.amount;
        });
        setLocalInventory(inventoryMap);
        setLoading(false);
      } catch (err) {
        console.error('API error:', err);
        setError('Failed to load bar data');
        setLoading(false);
      }
    };
    if (userData && userData.userid) {
      fetchData();
    } else {
      setError('User data is missing');
      setLoading(false);
    }
  }, [userData, apiClient]);

  const handleToggleFavorite = async (ingredientid, isFavorited) => {
    try {
      const method = isFavorited ? 'DELETE' : 'POST';
      await apiClient.request('/api/ingredients_favorites', {
        method,
        body: JSON.stringify({ userid: userData.userid, ingredientid }),
      });
      const response = await apiClient.request('/api/mybar', { method: 'GET' });
      console.log('Updated API response:', response);
      const sanitizedData = {
        total_ingredients: response.total_ingredients || 0,
        total_drinks: response.total_drinks || 0,
        ingredient_categories: Array.isArray(response.ingredient_categories) ? response.ingredient_categories : [],
        favorite_ingredients: Array.isArray(response.favorite_ingredients)
          ? response.favorite_ingredients.map(item => ({
              ...item,
              icon: item.icon || 'faWineBottle',
              icon_color: item.icon_color || 'ingredients-gray',
              type_name: item.type_name || 'Unknown',
            }))
          : [],
        ingredients: Array.isArray(response.ingredients)
          ? response.ingredients.map(item => ({
              ...item,
              icon: item.icon || 'faWineBottle',
              icon_color: item.icon_color || 'ingredients-gray',
              type_name: item.type_name || 'Unknown',
              amount: item.amount || 0,
            }))
          : [],
      };
      setData(sanitizedData);
      // Update localInventory with new amounts
      const inventoryMap = { ...localInventory };
      sanitizedData.ingredients.forEach(item => {
        inventoryMap[item.ingredientid] = item.amount;
      });
      setLocalInventory(inventoryMap);
    } catch (err) {
      console.error('Favorite toggle error:', err);
      setError('Failed to update favorite');
    }
  };

  const handleToggleInventory = async (ingredientid, isInInventory) => {
    try {
      const amount = isInInventory ? 0 : 750;
      await apiClient.request('/api/inventory', {
        method: 'POST',
        body: JSON.stringify({ userid: userData.userid, ingredientid, amount }),
      });
      // Update localInventory to reflect the new amount
      setLocalInventory(prev => ({
        ...prev,
        [ingredientid]: amount,
      }));
      // Refetch /api/mybar to update total_ingredients and total_drinks
      const response = await apiClient.request('/api/mybar', { method: 'GET' });
      console.log('Updated API response:', response);
      const sanitizedData = {
        total_ingredients: response.total_ingredients || 0,
        total_drinks: response.total_drinks || 0,
        ingredient_categories: Array.isArray(response.ingredient_categories) ? response.ingredient_categories : [],
        favorite_ingredients: Array.isArray(response.favorite_ingredients)
          ? response.favorite_ingredients.map(item => ({
              ...item,
              icon: item.icon || 'faWineBottle',
              icon_color: item.icon_color || 'ingredients-gray',
              type_name: item.type_name || 'Unknown',
            }))
          : [],
        ingredients: Array.isArray(response.ingredients)
          ? response.ingredients.map(item => ({
              ...item,
              icon: item.icon || 'faWineBottle',
              icon_color: item.icon_color || 'ingredients-gray',
              type_name: item.type_name || 'Unknown',
              amount: item.amount || 0,
            }))
          : [],
      };
      setData(sanitizedData);
      // Update localInventory, preserving the toggled amount
      setLocalInventory(prev => ({
        ...prev,
        ...Object.fromEntries(
          sanitizedData.ingredients.map(item => [item.ingredientid, item.amount])
        ),
        [ingredientid]: amount, // Ensure toggled amount is preserved
      }));
    } catch (err) {
      console.error('Inventory toggle error:', err);
      setError('Failed to update inventory');
    }
  };

  const filterIngredients = (items) =>
    items.filter(
      (item) =>
        (selectedCategory === null || item.category === selectedCategory) &&
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Merge data.ingredients with localInventory to include amount = 0
  const allIngredients = [
    ...data.ingredients,
    ...Object.entries(localInventory)
      .filter(([id, amount]) => amount === 0 && !data.ingredients.some(i => i.ingredientid === parseInt(id)))
      .map(([id]) => ({
        ingredientid: parseInt(id),
        name: data.favorite_ingredients.find(f => f.ingredientid === parseInt(id))?.name || 'Unknown',
        category: data.favorite_ingredients.find(f => f.ingredientid === parseInt(id))?.category || 'Other',
        type_name: data.favorite_ingredients.find(f => f.ingredientid === parseInt(id))?.type_name || 'Unknown',
        icon: data.favorite_ingredients.find(f => f.ingredientid === parseInt(id))?.icon || 'faWineBottle',
        icon_color: data.favorite_ingredients.find(f => f.ingredientid === parseInt(id))?.icon_color || 'ingredients-gray',
        amount: 0,
      })),
  ];

  const filteredFavorites = filterIngredients(data.favorite_ingredients);
  const filteredIngredients = filterIngredients(allIngredients);
  const groupedIngredients = filteredIngredients.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center items-center h-screen">Loading...</div>;
  if (error) return <div className="text-red-500 text-center">{error}</div>;

  return (
    <div className="my-bar-container">
      <div className="my-bar-gradient-bg"></div>
      <div className="content-container">
        {/* Header */}
        <header className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">My Bar</h1>
            <p className="text-gray-300 text-sm">Manage your ingredients</p>
          </div>
          <div className="flex space-x-3">
            <button className="w-10 h-10 rounded-full bg-dark-800 flex items-center justify-center">
              <FontAwesomeIcon icon={faWineGlassAlt} className="text-gray-300" />
            </button>
            <button className="w-10 h-10 rounded-full bg-dark-800 flex items-center justify-center">
              <FontAwesomeIcon icon={faEllipsisV} className="text-gray-300" />
            </button>
          </div>
        </header>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="card rounded-xl p-4 text-center">
            <h3 className="text-2xl font-bold text-primary mb-1">{data.total_ingredients}</h3>
            <p className="text-xs text-gray-300">Ingredients</p>
          </div>
          <div className="card rounded-xl p-4 text-center">
            <h3 className="text-2xl font-bold text-secondary mb-1">{data.total_drinks}</h3>
            <p className="text-xs text-gray-300">Available Drinks</p>
          </div>
          <div className="card rounded-xl p-4 text-center">
            <h3 className="text-2xl font-bold text-emerald-400 mb-1">0</h3>
            <p className="text-xs text-gray-300">Shopping List</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-5">
          <div className="search-bar flex items-center rounded-xl px-4 py-3">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-3" />
            <input
              type="text"
              placeholder="Search ingredients..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="filter-btn">
              <FontAwesomeIcon icon={faSlidersH} />
            </button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="mb-6">
          <div className="flex border-b border-gray-700 overflow-x-auto">
            <button
              className={`tab-item ${selectedCategory === null ? 'active' : 'text-gray-400'} whitespace-nowrap px-4 py-3 font-medium border-b-2 border-transparent`}
              onClick={() => setSelectedCategory(null)}
            >
              All Ingredients
            </button>
            {data.ingredient_categories.map((category) => (
              <button
                key={category.categoryid}
                className={`tab-item ${selectedCategory === category.name ? 'active' : 'text-gray-400'} whitespace-nowrap px-4 py-3 font-medium border-b-2 border-transparent`}
                onClick={() => setSelectedCategory(category.name)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Ingredient List */}
        {filteredFavorites.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3">Favorites</h2>
            <ul className="space-y-2">
              {filteredFavorites.map((item) => (
                <li key={item.ingredientid} className="ingredient-item flex items-center justify-between rounded-xl p-3">
                  <div className="flex items-center">
                    <div className={"ingredients-icon " + item.icon_color + "-bg"}>
                      <FontAwesomeIcon
                        icon={iconMap[item.icon] || faWineBottle}
                        className={item.icon_color}
                      />
                    </div>
                    <div>
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-xs text-gray-400">{item.type_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      className={`ingredient-bookmark-btn ${data.favorite_ingredients.some(f => f.ingredientid === item.ingredientid) ? 'text-primary' : 'text-gray-400'}`}
                      onClick={() => handleToggleFavorite(item.ingredientid, data.favorite_ingredients.some(f => f.ingredientid === item.ingredientid))}
                    >
                      <FontAwesomeIcon icon={faBookmark} />
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(localInventory[item.ingredientid] || 0) > 0}
                        onChange={(e) => handleToggleInventory(item.ingredientid, (localInventory[item.ingredientid] || 0) > 0)}
                        className="sr-only peer"
                      />
                      <div className="toggle-bg w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-primary">
                        <div className="toggle-dot absolute w-4 h-4 rounded-full bg-white left-1 top-1 peer-checked:translate-x-5 transition-transform"></div>
                      </div>
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {Object.entries(groupedIngredients).map(([category, items]) => (
          <div key={category} className="mb-6">
            <h2 className="text-lg font-semibold mb-3">{category}</h2>
            <ul className="space-y-2">
              {items.map((item) => (
                <li key={item.ingredientid} className="ingredient-item flex items-center justify-between rounded-xl p-3">
                  <div className="flex items-center">
                    <div className={"ingredients-icon " + item.icon_color + "-bg"}>
                      <FontAwesomeIcon
                        icon={iconMap[item.icon] || faWineBottle}
                        className={item.icon_color}
                      />
                    </div>
                    <div>
                      <h3 className="font-medium">{item.name}</h3>
                      <p className="text-xs text-gray-400">{item.type_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      className={`ingredient-bookmark-btn ${data.favorite_ingredients.some(f => f.ingredientid === item.ingredientid) ? 'text-primary' : 'text-gray-400'}`}
                      onClick={() => handleToggleFavorite(item.ingredientid, data.favorite_ingredients.some(f => f.ingredientid === item.ingredientid))}
                    >
                      <FontAwesomeIcon icon={faBookmark} />
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(localInventory[item.ingredientid] || 0) > 0}
                        onChange={(e) => handleToggleInventory(item.ingredientid, (localInventory[item.ingredientid] || 0) > 0)}
                        className="sr-only peer"
                      />
                      <div className="toggle-bg w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-primary">
                        <div className="toggle-dot absolute w-4 h-4 rounded-full bg-white left-1 top-1 peer-checked:translate-x-5 transition-transform"></div>
                      </div>
                    </label>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MyBarPage;